import soundVisualizerFinished from "../../images/engineering portfolio pictures/sound visualizer/soundvisualizer.jpg";
import soundVisualizerBandpass from "../../images/engineering portfolio pictures/sound visualizer/bandpassstage.png";
import soundVisualizerBode from "../../images/engineering portfolio pictures/sound visualizer/bodeplots.png";
import soundVisualizerHousing from "../../images/engineering portfolio pictures/sound visualizer/cadhousing.png";
import soundVisualizerLedDriver from "../../images/engineering portfolio pictures/sound visualizer/leddriver.png";
import soundVisualizerPcb from "../../images/engineering portfolio pictures/sound visualizer/pcb.png";

import sunriseFinished from "../../images/engineering portfolio pictures/sunrise lamp/sunrise lamp 1.png";
import sunriseSkeleton from "../../images/engineering portfolio pictures/sunrise lamp/rgb skeleton.png";
import sunriseTesting from "../../images/engineering portfolio pictures/sunrise lamp/sunrise lamp testing.png";
import sunriseCad from "../../images/engineering portfolio pictures/sunrise lamp/cad.png";
import sunrisePcbMilling from "../../images/engineering portfolio pictures/sunrise lamp/pcb milling.png";
import sunrisePcbWork from "../../images/engineering portfolio pictures/sunrise lamp/pcb work.png";

import waveawayFinished from "../../images/engineering portfolio pictures/waveaway/waveaway.png";
import waveawayInternals from "../../images/engineering portfolio pictures/waveaway/internals.png";
import waveawayCasing from "../../images/engineering portfolio pictures/waveaway/casing.png";
import waveawayExploded from "../../images/engineering portfolio pictures/waveaway/exploded view.png";

import rcVersion3 from "../../images/engineering portfolio pictures/rc car/rc car version 3.png";
import rcVersion2 from "../../images/engineering portfolio pictures/rc car/rc car version 2.jpg";
import rcVersion1 from "../../images/engineering portfolio pictures/rc car/rc car version 1.jpg";
import rcCad from "../../images/engineering portfolio pictures/rc car/cad.png";
import rcInternals from "../../images/engineering portfolio pictures/rc car/carinternals.png";
import rcLaserCut from "../../images/engineering portfolio pictures/rc car/lasercut.png";
import rcPcb from "../../images/engineering portfolio pictures/rc car/pcb.png";

import kineticFinal from "../../images/engineering portfolio pictures/kinetic sculpture/final version.png";
import kineticCad from "../../images/engineering portfolio pictures/kinetic sculpture/cad.png";
import kineticLaserCut from "../../images/engineering portfolio pictures/kinetic sculpture/laser cut.png";
import kineticLaserCut2 from "../../images/engineering portfolio pictures/kinetic sculpture/laser cut 2.png";

import motionMinderFinished from "../../images/engineering portfolio pictures/motion minder/motionminder.jpg";
import motionMinderLasers from "../../images/engineering portfolio pictures/motion minder/laser array.jpg";
import motionMinderMux from "../../images/engineering portfolio pictures/motion minder/multiplexer soldering.jpg";

import roboticLiftFinished from "../../images/engineering portfolio pictures/robotic lift/roboticlift.jpg";
import roboticLiftGears from "../../images/engineering portfolio pictures/robotic lift/metal gears.jpg";
import roboticLiftCasting from "../../images/engineering portfolio pictures/robotic lift/casting.jpg";

import hsClock from "../../images/engineering portfolio pictures/hs projects/clock.jpg";
import hsGeneva from "../../images/engineering portfolio pictures/hs projects/geneva wheel.jpg";
import hsLedSign from "../../images/engineering portfolio pictures/hs projects/ledsign.jpg";
import hsSmartMirror from "../../images/engineering portfolio pictures/hs projects/smartmirror.jpg";

export type EngineeringImage = {
  src: string;
  alt: string;
  caption: string;
  body?: string;
};

export type EngineeringProjectData = {
  slug: string;
  name: string;
  summary: string;
  cardImage: string;
  images: EngineeringImage[];
};

export const engineeringProjects: EngineeringProjectData[] = [
  {
    slug: "sound-visualizer",
    name: "Sound Visualizer",
    summary:
      "An analog sound visualizer that splits audio into frequency bands and displays them on an LED matrix. The build includes LM348 band-pass stages, Bode plots of the filter bands, a logarithmic LED-driver board, and a custom enclosure.",
    cardImage: soundVisualizerFinished,
    images: [
      {
        src: soundVisualizerFinished,
        alt: "Finished sound visualizer with a lit LED matrix",
        caption: "Finished visualizer, with the LED matrix responding to audio",
      },
      {
        src: soundVisualizerBandpass,
        alt: "LTSpice schematic of a 2000 to 4000 Hz band-pass stage",
        caption: "Band-pass stage for the 2000–4000 Hz band, using LM348 op-amps",
      },
      {
        src: soundVisualizerBode,
        alt: "Bode plots of five band-pass filters",
        caption: "Bode plots of the five filter bands",
      },
      {
        src: soundVisualizerHousing,
        alt: "CAD model of the sound visualizer housing",
        caption: "CAD model of the LED housing",
      },
      {
        src: soundVisualizerLedDriver,
        alt: "PCB layout for the LED driver",
        caption: "LED driver board that feeds the light columns",
      },
      {
        src: soundVisualizerPcb,
        alt: "Sound visualizer printed circuit board",
        caption: "Sound visualizer PCB",
      },
    ],
  },
  {
    slug: "sunrise-lamp",
    name: "Sunrise Lamp",
    summary:
      "A spherical lamp built from rings of addressable RGB LEDs. A custom image-processing algorithm turns a photo from any time of day into a sunrise animation. The lamp talks to a website over WiFi, using GET and POST requests to transfer that data.",
    cardImage: sunriseFinished,
    images: [
      {
        src: sunriseFinished,
        alt: "Finished sunrise lamp glowing as a warm globe",
        caption: "Finished sunrise lamp",
      },
      {
        src: sunriseSkeleton,
        alt: "Rings of addressable RGB LEDs before the diffuser",
        caption: "RGB LED skeleton before the globe goes on",
      },
      {
        src: sunriseTesting,
        alt: "Sunrise lamp during testing",
        caption: "Testing the lamp",
      },
      {
        src: sunriseCad,
        alt: "CAD model of the sunrise lamp",
        caption: "CAD model of the lamp",
      },
      {
        src: sunrisePcbMilling,
        alt: "PCB being milled for the sunrise lamp",
        caption: "Milling the lamp PCB",
      },
      {
        src: sunrisePcbWork,
        alt: "Sunrise lamp PCB being assembled",
        caption: "Working on the sunrise lamp PCB",
      },
    ],
  },
  {
    slug: "waveaway",
    name: "WaveAway",
    summary:
      "A safer way to change music in older cars that do not have steering-wheel controls. Waving a hand over the device skips, rewinds, pauses, and changes the volume without looking away from the road.",
    cardImage: waveawayFinished,
    images: [
      {
        src: waveawayFinished,
        alt: "WaveAway showing a song name on its screen",
        caption: "Finished WaveAway, showing the current song",
      },
      {
        src: waveawayInternals,
        alt: "Battery, display, and microcontroller inside WaveAway",
        caption: "Internals: battery, display, and microcontroller",
      },
      {
        src: waveawayCasing,
        alt: "3D-printed WaveAway casing",
        caption: "3D-printed casing",
      },
      {
        src: waveawayExploded,
        alt: "Exploded view of the WaveAway assembly",
        caption: "Exploded view of the assembly",
      },
    ],
  },
  {
    slug: "rc-car",
    name: "RC Car",
    summary:
      "An RC car built without a microcontroller. It uses a custom radio transmitter and receiver, plus a custom PWM generator that sets the steering servo position.",
    cardImage: rcVersion3,
    images: [
      {
        src: rcVersion3,
        alt: "Third version of the RC car with its transmitter",
        caption: "Version 3, with the two-joystick transmitter",
      },
      {
        src: rcVersion2,
        alt: "Second version of the RC car",
        caption: "Version 2",
      },
      {
        src: rcVersion1,
        alt: "First version of the RC car",
        caption: "Version 1",
      },
      {
        src: rcCad,
        alt: "CAD model of the RC car",
        caption: "CAD model of the car",
      },
      {
        src: rcInternals,
        alt: "Internal wiring and drive of the RC car",
        caption: "Car internals",
      },
      {
        src: rcLaserCut,
        alt: "Laser-cut parts for the RC car",
        caption: "Laser-cut chassis parts",
      },
      {
        src: rcPcb,
        alt: "Milled copper PCB for the RC car",
        caption: "Milled copper control PCB",
      },
    ],
  },
  {
    slug: "kinetic-sculpture",
    name: "Kinetic Sculpture",
    summary:
      "Made entirely of laser-cut MDF and designed to assemble without glue. Two spiral discs turn in opposite directions, driven by a custom 1:−1 gearbox with a 10:1 reduction on the motor.",
    cardImage: kineticFinal,
    images: [
      {
        src: kineticFinal,
        alt: "Finished kinetic sculpture with two spiral discs",
        caption: "Finished sculpture",
      },
      {
        src: kineticCad,
        alt: "CAD model of the kinetic sculpture gear train and motor",
        caption: "CAD model of the motor and gear train",
      },
      {
        src: kineticLaserCut,
        alt: "Laser-cut pieces for the kinetic sculpture",
        caption: "Laser-cut spiral pieces",
      },
      {
        src: kineticLaserCut2,
        alt: "Additional laser-cut pieces for the kinetic sculpture",
        caption: "More laser-cut pieces",
      },
    ],
  },
  {
    slug: "motion-minder",
    name: "Motion Minder",
    summary:
      "Built to mimic Apple's motion cues for physical literature. A laser grid and a 9-axis IMU detect how the surroundings are moving and project patterns onto the page, which helps reduce motion sickness for the reader.",
    cardImage: motionMinderFinished,
    images: [
      {
        src: motionMinderFinished,
        alt: "Assembled Motion Minder on its stand",
        caption: "Assembled Motion Minder",
      },
      {
        src: motionMinderLasers,
        alt: "Grid of red laser diodes",
        caption: "Laser array",
      },
      {
        src: motionMinderMux,
        alt: "Multiplexer board being soldered",
        caption: "Soldering the multiplexer",
      },
    ],
  },
  {
    slug: "robotic-lift",
    name: "Robotic Lift",
    summary:
      "A lift for reaching the underside of FTC robots, similar to a car lift. I cast metal gears so the motors could hold a robot of about 40 pounds. The project was a lesson in picking materials and a design that can actually carry the load.",
    cardImage: roboticLiftFinished,
    images: [
      {
        src: roboticLiftFinished,
        alt: "Two wooden linear lifts with belt drives",
        caption: "The two lift columns",
      },
      {
        src: roboticLiftGears,
        alt: "Two machined metal pinion gears",
        caption: "Metal gears for the lift motors",
      },
      {
        src: roboticLiftCasting,
        alt: "Cast cylinder with a metal insert",
        caption: "Casting gears strong enough for a robot of about 40 pounds",
      },
    ],
  },
  {
    slug: "high-school-projects",
    name: "High School Projects",
    summary:
      "Four shop projects: a machined metal clock, a wooden Geneva wheel, an edge-lit sign, and a smart mirror I still use for the weather and my calendar.",
    cardImage: hsClock,
    images: [
      {
        src: hsClock,
        alt: "Machined metal wall clock with a scroll pattern",
        caption: "Machined metal wall clock",
      },
      {
        src: hsGeneva,
        alt: "Wooden Geneva wheel mechanism",
        caption: "Geneva wheel",
        body: "A wooden Geneva wheel that produces intermittent motion, the same kind of motion used in watches.",
      },
      {
        src: hsLedSign,
        alt: "Edge-lit acrylic sign of a skier with colored buttons",
        caption: "Light-up sign",
        body: "This project showed me the value of iteration and compromise. I wanted a custom light-up sign with several modes. The original plan was to cycle through 7 colors, which I could not finish with the methods available at school. That is what inspired me to take a Gifted and Talented course the next year. I did get the sign working, but I had to redefine what success meant in order to finish it.",
      },
      {
        src: hsSmartMirror,
        alt: "Smart mirror showing the time, calendar, and weather",
        caption: "Smart mirror",
        body: "I made this mirror after seeing someone describe the project while scrolling one night before bed. It challenged me to work with outdated tech and software limits, and it reminded me that resourcefulness matters more than perfection. I kept at it and ended up with a mirror I actually use every day to check the weather and my calendar. Even though the interface was not my own, the project taught me how to build something that serves a real daily purpose.",
      },
    ],
  },
];

export const engineeringCards = engineeringProjects.map((project) => [
  project.name,
  project.summary,
  project.cardImage,
  `/engineering/${project.slug}`,
]);
